import type { APIResponse } from './api';

export type OutputFormat = 'ddex-ern' | 'apple-itunes';

export type ReleaseProfile = 'Audio' | 'Video' | 'LongFormMusicalWorkVideo' | 'DjMix';

// ReleaseType based on DDEX AVS (Allowed Value Set) for ERN 4.x
export type ReleaseType = 
    | 'Album'
    | 'Single'
    | 'SingleResourceRelease'
    | 'SingleResourceReleaseWithCoverArt'
    | 'EP'
    | 'DjMix'
    | 'VideoSingle'
    | 'ClassicalAlbum'
    | 'AudioBookRelease'
    | 'LongFormMusicalWorkVideoRelease'
    | 'ConcertVideo'
    | 'Documentary';

export interface ReleaseRequest {
    outputFormat?: OutputFormat;
    ernVersion: string;
    avsVersion: number;
    releaseProfile: ReleaseProfile;
    releaseType: ReleaseType;
    releaseId?: ReleaseId;
    generateAiAssets: boolean;
}

export interface ProprietaryId {
    namespace: string;
    value: string;
}

export interface ReleaseId {
    ICPN: string;
    proprietaryId?: ProprietaryId;
}

export interface ReleaseMetadata {
    artist: string;
    title: string;
    genre: string;
}

/**
 * MessageHeader contains sender/recipient information from ERN
 * Used to populate manifest files during delivery
 */
export interface MessageHeader {
    messageId: string;
    senderPartyId: string;
    senderName: string;
    recipientPartyId: string;
    recipientName: string;
    createdDateTime: string;
}

export interface ReleaseData {
    xml: string;
    releaseId: ReleaseId;
    releaseMetadata?: ReleaseMetadata;
    messageHeader?: MessageHeader;
    releaseProfile: ReleaseProfile;
    // Apple-specific: vendor ID used as delivery identifier
    deliveryId?: string;
}

export interface AssetRequest {
    releaseId: ReleaseId;
    releaseProfile: ReleaseProfile;
    releaseMetadata: ReleaseMetadata;
}

export interface AssetData {
    imageUrl?: string | null;
    audioUrl?: string | null;
    videoUrl?: string | null;
}

// Job management types for asset generation
export interface AssetJobData {
    jobId: string;
}

export type AssetJobResponse = APIResponse<AssetJobData>;

export interface AssetProgressEvent {
    message: string;
    progress?: number;
}

export interface AssetCompleteEvent {
    results: AssetData;
}

export interface AssetErrorEvent {
    error: string;
}

export type ReleaseResponse = APIResponse<ReleaseData>; 
export type AssetResponse = APIResponse<AssetData>;

// Helper functions for ReleaseType
// NOTE: Only return ReleaseTypes that have corresponding templates implemented
export function getReleaseTypesForProfile(profile: ReleaseProfile, format?: OutputFormat): ReleaseType[] {
    if (format === 'apple-itunes') {
        // Apple has no SingleResourceRelease, Video, or profile-specific types
        return ['Single', 'EP', 'Album'];
    }

    switch (profile) {
        case 'Audio':
            return [
                'Single',
                'SingleResourceRelease',
                // TODO: Uncomment when templates are implemented:
                // 'SingleResourceReleaseWithCoverArt',
                // 'Album',
                // 'EP',
                // 'DjMix',
                // 'ClassicalAlbum',
                // 'AudioBookRelease',
            ];
        case 'Video':
            return [
                'VideoSingle',
                'SingleResourceRelease',
                // TODO: Uncomment when templates are implemented:
                // 'SingleResourceReleaseWithCoverArt',
                // 'ConcertVideo',
                // 'Documentary',
            ];
        // TODO: Re-enable when templates are fixed:
        // case 'LongFormMusicalWorkVideo':
        //     return [
        //         'LongFormMusicalWorkVideoRelease',
        //         'SingleResourceRelease',
        //     ];
        // case 'DjMix':
        //     return ['DjMix'];
        default:
            return ['Single'];
    }
}

export function getDefaultReleaseType(profile: ReleaseProfile): ReleaseType {
    switch (profile) {
        case 'Audio':
            return 'Single';
        case 'Video':
            return 'VideoSingle';
        // TODO: Re-enable when templates are fixed:
        // case 'LongFormMusicalWorkVideo':
        //     return 'LongFormMusicalWorkVideoRelease';
        // case 'DjMix':
        //     return 'DjMix';
        default:
            return 'Single';
    }
}

export function getReleaseTypeDescription(releaseType: ReleaseType): string {
    switch (releaseType) {
        case 'Single':
            return 'Short duration, typically 1-2 tracks';
        case 'SingleResourceRelease':
            return 'A release containing a single resource';
        case 'SingleResourceReleaseWithCoverArt':
            return 'Single resource with cover art image';
        case 'Album':
            return 'Extended duration with multiple tracks';
        case 'EP':
            return 'Mid-size release between a single and album';
        case 'DjMix':
            return 'Mixed from several sound recordings';
        case 'VideoSingle':
            return 'Short duration, typically 1-2 videos';
        case 'ClassicalAlbum':
            return 'Album containing classical musical works';
        case 'AudioBookRelease':
            return 'Audio recording of a book or manuscript';
        case 'LongFormMusicalWorkVideoRelease':
            return 'Long form musical work video';
        case 'ConcertVideo':
            return 'Video recording of a live performance';
        case 'Documentary':
            return 'Documentary video content';
        default:
            return 'The type of release being created';
    }
} 